
import { defineComponent, onMounted, reactive, ref, watch } from 'vue';
import ModalLayout from '@/layouts/ModalLayout.vue';
import UserListItem from '@/components/UserListItemElement.vue';
import { popModal, pushModal } from 'jenesius-vue-modal';
import useRequest from '@/composables/useRequest';
import { LeaveTeamRequest } from '@/data/requests/LeaveTeamRequest';
import ButtonElement from '@/components/ButtonElement.vue';
import { TeamMember } from '@/data/models/TeamMember';
import { useI18n } from 'vue-i18n';
import { GetTeamStatusRequest } from '@/data/requests/GetTeamStatusRequest';
import { TeamStatusRequestResponse } from '@/data/models/TeamStatusRequestResponse';
import _ from 'lodash';
import { TeamMemberRequestResponse } from '@/data/models/TeamMemberRequestResponse';
import { PendingInviteRequestResponse } from '@/data/models/PendingInviteRequestResponse';
import { Team } from '@/data/models/Team';
import SearchUsersModal from '@/modals/SearchUsersModal.vue';
import { UpdateTeamNameRequest } from '@/data/requests/UpdateTeamNameRequest';
import InputElement from '@/components/InputElement.vue';
import useConvertPictureURLToResizedURL from '@/composables/useConvertPictureURLToResizedURL';
import useEventStatus from '@/composables/useEventStatus';

export default defineComponent({
  components: { ModalLayout, UserListItem, ButtonElement, InputElement },

  props: {
    teamName: {
      type: String,
      required: true,
    },
    refreshHomePage: {
      type: Function,
      required: true,
    },
    editTeam: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const { t } = useI18n();

    const { hasStarted, hasEnded, isLive } = useEventStatus();
    console.log(hasStarted, hasEnded, isLive);

    const team = reactive<Team>({
      name: _.cloneDeep(props.teamName),
      leader: {} as TeamMember,
      members: [] as TeamMember[],
      pending_invites: [] as TeamMember[],
    });

    const oldTeamName = team.name;

    const isEditing = ref(props.editTeam);
    const {
      loading: loadingGetTeamStatus,
      error: errorGetTeamStatus,
      execute: executeGetTeamStatus,
    } = useRequest<GetTeamStatusRequest, TeamStatusRequestResponse>(new GetTeamStatusRequest(), t('errors.team.cannot_get_status'));

    const refreshTeam = () => {
      executeGetTeamStatus().then((response: TeamStatusRequestResponse) => {
        team.name = '';
        team.is_leader = false;
        team.leader = {} as TeamMember;
        team.members = [];
        team.pending_invites = [];

        if (response.team?.team_name !== undefined) {
          team.name = response.team.team_name;

          const teamLeaderData = response.team.leader;

          team.is_leader = teamLeaderData.imleader;

          team.leader = {
            user_id: teamLeaderData.id,
            name: `${teamLeaderData.first_name} ${teamLeaderData.last_name}`,
            profile_picture_url: useConvertPictureURLToResizedURL(teamLeaderData.picture_url),
            is_captain: true,
          };

          response.team.members.forEach((teamMember: TeamMemberRequestResponse) => {
            team.members?.push({
              user_id: teamMember.id,
              name: `${teamMember.first_name} ${teamMember.last_name}`,
              profile_picture_url: useConvertPictureURLToResizedURL(teamMember.picture_url),
            });
          });

          response.team.invites?.forEach((pendingInvite: PendingInviteRequestResponse) => {
            team.pending_invites?.push({
              user_id: pendingInvite.user_id,
              name: `${pendingInvite.first_name} ${pendingInvite.last_name}`,
              profile_picture_url: useConvertPictureURLToResizedURL(pendingInvite.picture_url),
              is_pending: true,
              invite_id: pendingInvite.id,
            });
          });
        }
      });
    };

    refreshTeam();

    const {
      loading: loadingLeaveTeam,
      error: errorLeaveTeam,
      execute: executeLeaveTeam,
    } = useRequest<LeaveTeamRequest, unknown>(new LeaveTeamRequest(), t('errors.team.cannot_leave'));

    const doLeaveTeam = () => {
      if (confirm(t('modals.team.alerts.confirm_leave_team'))) {
        executeLeaveTeam().then(async () => {
          await props.refreshHomePage();
          popModal();
        });
      }
    };

    const openSearchUsersModal = () => {
      pushModal(SearchUsersModal, {
        refreshTeam: refreshTeam,
      });
    };

    const loadingUpdateTeamName = ref(false);
    const errorUpdateTeamName = ref<string[] | undefined>();

    const doUpdateTeamName = () => {
      loadingUpdateTeamName.value = true;
      errorUpdateTeamName.value = undefined;

      if ((team.name ?? '').length > 30) {
        errorUpdateTeamName.value = [];
        errorUpdateTeamName.value?.push(t('errors.team.max_length'));
        loadingUpdateTeamName.value = false;
      } else {
        var regex = new RegExp('^[A-Za-z0-9]*$');

        if (regex.test(team.name ?? '')) {
          const { execute } = useRequest<UpdateTeamNameRequest, never>(new UpdateTeamNameRequest(team.name ?? ''), '');

          execute()
            .then(() => {
              loadingUpdateTeamName.value = false;
              isChangeNameButtonDisabled.value = true;
            })
            .catch(() => {
              errorUpdateTeamName.value = [];
              errorUpdateTeamName.value?.push(t('errors.team.name_taken'));
              loadingUpdateTeamName.value = false;
            });
        } else {
          errorUpdateTeamName.value = [];
          errorUpdateTeamName.value?.push(t('errors.team.invalid_characters'));
          loadingUpdateTeamName.value = false;
        }
      }
    };

    const { execute: executeDeleteTeam, loading: loadingDeleteTeam } = useRequest<LeaveTeamRequest, never>(
      new LeaveTeamRequest(),
      t('errors.team.cannot_delete')
    );

    const errorDeleteTeam = ref<string | undefined>();

    const doDeleteTeam = () => {
      if (confirm(t('modals.team.alerts.confirm_delete_team'))) {
        errorDeleteTeam.value = undefined;
        if ((team.members?.length ?? 0) > 0 || (team.pending_invites?.length ?? 0) > 0) {
          errorDeleteTeam.value = t('errors.team.delete_members_first');
        } else {
          executeDeleteTeam()
            .then(() => {
              popModal();
              props.refreshHomePage();
            })
            .catch(() => {
              errorDeleteTeam.value = t('errors.team.cannot_delete');
            });
        }
      }
    };

    const errorKickMember = ref<string | undefined>();

    const editErrorKickMember = (newError: string) => {
      errorKickMember.value = newError;
    };

    const isChangeNameButtonDisabled = ref<boolean>(true);

    watch(team, () => {
      const length = team.name?.length ?? 0;

      isChangeNameButtonDisabled.value = !(length > 3 && team.name !== oldTeamName);
    });

    return {
      doLeaveTeam,
      loadingLeaveTeam,
      errorLeaveTeam,
      isEditing,
      team,
      openSearchUsersModal,
      doUpdateTeamName,
      loadingUpdateTeamName,
      errorUpdateTeamName,
      doDeleteTeam,
      loadingDeleteTeam,
      loadingGetTeamStatus,
      errorDeleteTeam,
      editErrorKickMember,
      refreshTeam,
      errorGetTeamStatus,
      hasEnded,
      isLive,
      isChangeNameButtonDisabled,
      oldTeamName,
    };
  },
});
